import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listGroupConfigurationsByClassId } from "../../api/groupConfigurationApi";
import {
  listSubjectConfigurations,
  createSubjectConfiguration,
  updateSubjectConfigurationsBulk,
  deleteSubjectConfiguration,
} from "../../api/subjectConfigurationApi";
import "../Attendance/Attendance.css";

const classApi = createLookupApi("classes");
const subjectApi = createLookupApi("subjects");

const SUBJECT_STATUS_OPTIONS = [
  { value: 0, label: "Compulsory" },
  { value: 1, label: "Choosable" },
  { value: 2, label: "Group Based" },
  { value: 4, label: "Uncountable" },
];

/**
 * digicampusbd's "Initial Setup > Subject Setup" screen
 * (`initial-setup-subject-configuration.html`) — assigns subjects to a
 * class×group combination and marks each as Compulsory/Choosable/Group
 * Based/Uncountable ("subject_status"). This decides, for every subject on
 * every mark-entry/report/tabulation screen in the Result Management family,
 * which group of students actually take it (e.g. a "Group Based" subject
 * like Accounting only shows Commerce-group students) — see
 * `SubjectConfiguration` entity javadoc for the full picture and why this
 * was missing from the migration until now.
 *
 * Matches the old app's own real flow exactly: pick a Class, pick one of
 * that class's already-configured Groups (sourced from
 * `GET /api/group-configurations/by-class/:id`, same as the old app's own
 * `/group/configuration/list/by/class-id` — not the plain group lookup list,
 * since a class only offers subjects for groups it's actually configured
 * with), check which subjects apply, Save (all start as Compulsory). The
 * resulting table is then edited in place (status/serial/merge-id per row)
 * and posted back in one "Update" call — same two-step create-then-refine
 * flow as the old app's own inline-editable table.
 *
 * Not yet done in this same change (deliberately, see MIGRATION.md): wiring
 * `ExamMarkService`/result-processing/blank-sheet/tabulation to actually
 * consult this configuration when scoping which students see a subject.
 */
export default function SubjectConfigurationPage() {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [groups, setGroups] = useState([]);
  const [classId, setClassId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [checkedSubjectIds, setCheckedSubjectIds] = useState([]);
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    classApi.list().then(setClasses);
    subjectApi.list().then(setSubjects);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTable = (cId, gId) => {
    if (!cId || !gId) {
      setRows([]);
      return;
    }
    setLoadingRows(true);
    listSubjectConfigurations(cId, gId)
      .then((data) => setRows(data.map((r) => ({ ...r }))))
      .catch(() => setError("Failed to load subject configuration."))
      .finally(() => setLoadingRows(false));
  };

  const handleClassChange = (e) => {
    const id = e.target.value;
    setClassId(id);
    setGroupId("");
    setRows([]);
    setGroups([]);
    if (id) {
      listGroupConfigurationsByClassId(id).then(setGroups);
    }
  };

  const handleGroupChange = (e) => {
    const gId = e.target.value;
    setGroupId(gId);
    loadTable(classId, gId);
  };

  const toggleSubject = (id) =>
    setCheckedSubjectIds((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  const handleSave = async () => {
    setError("");
    setSuccess("");
    if (!classId || !groupId) {
      setError("Select a class and a group.");
      return;
    }
    if (checkedSubjectIds.length === 0) {
      setError("Select at least one subject.");
      return;
    }
    try {
      await createSubjectConfiguration({
        classId: Number(classId),
        groupId: Number(groupId),
        subjectIds: checkedSubjectIds.map(Number),
      });
      setSuccess("Subject Configuration Saved.");
      setCheckedSubjectIds([]);
      loadTable(classId, groupId);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save.");
    }
  };

  const updateRowField = (id, field, value) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const handleUpdate = async () => {
    setError("");
    setSuccess("");
    try {
      await updateSubjectConfigurationsBulk(
        rows.map((r) => ({
          id: r.id,
          subjectSerial: Number(r.subjectSerial),
          subjectStatus: Number(r.subjectStatus),
          subjectMergeId: Number(r.subjectMergeId) || 0,
        }))
      );
      setSuccess("Subject Configuration Updated.");
      loadTable(classId, groupId);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to update.");
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remove "${row.subjectName}" from this class/group?`)) return;
    try {
      await deleteSubjectConfiguration(row.id);
      setSuccess("Subject Configuration Successfully Deleted.");
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  const configuredSubjectIds = new Set(rows.map((r) => r.subjectId));
  const assignableSubjects = subjects.filter((s) => !configuredSubjectIds.has(s.id));

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Subject Setup</h1>
      </div>
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <div className="attendance-filters">
        <label>
          Class List
          <select value={classId} onChange={handleClassChange}>
            <option value="">Select Class</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Group List
          <select value={groupId} onChange={handleGroupChange} disabled={!classId}>
            <option value="">Select Group</option>
            {groups.map((g) => (
              <option key={g.groupId} value={g.groupId}>
                {g.groupName}
              </option>
            ))}
          </select>
        </label>
      </div>

      {classId && groupId && (
        <>
          <h3 style={{ marginTop: 16 }}>Assign Subjects</h3>
          {assignableSubjects.length === 0 ? (
            <p>Every subject is already configured for this class/group.</p>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 24px", margin: "12px 0" }}>
              {assignableSubjects.map((s) => (
                <label key={s.id} className="checkbox-label" style={{ display: "inline-flex", minWidth: 180 }}>
                  <input
                    type="checkbox"
                    checked={checkedSubjectIds.includes(s.id)}
                    onChange={() => toggleSubject(s.id)}
                  />{" "}
                  {s.name}
                </label>
              ))}
            </div>
          )}
          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={handleSave}>
              Save
            </button>
          </div>

          <h3 style={{ marginTop: 24 }}>Configured Subjects</h3>
          {loadingRows ? (
            <p>Loading...</p>
          ) : rows.length === 0 ? (
            <p>No subjects configured yet for this class/group.</p>
          ) : (
            <>
              <div className="table-scroll">
                <table className="attendance-table">
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th>Subject Type</th>
                      <th>Subject Serial</th>
                      <th>Merge ID</th>
                      <th>Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td>{r.subjectName}</td>
                        <td>
                          <select
                            value={r.subjectStatus}
                            onChange={(e) => updateRowField(r.id, "subjectStatus", e.target.value)}
                          >
                            {SUBJECT_STATUS_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            value={r.subjectSerial}
                            onChange={(e) => updateRowField(r.id, "subjectSerial", e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={r.subjectMergeId}
                            onChange={(e) => updateRowField(r.id, "subjectMergeId", e.target.value)}
                          />
                        </td>
                        <td>
                          <button type="button" className="btn-link danger" onClick={() => handleDelete(r)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-primary" onClick={handleUpdate}>
                  Update
                </button>
              </div>
            </>
          )}
        </>
      )}
    </MainLayout>
  );
}
